import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { imageUrl } from "../lib/cloudinary";

/**
 * Premium auto-sliding hero carousel.
 * - Smooth translateX slide transition
 * - Auto-advances every 3s, infinite loop
 * - Touch swipe support (mobile)
 * - Dot navigation
 * - Pauses on hover (desktop) and during touch (mobile)
 * - Lazy-loads off-screen slide images
 */
export default function HeroCarousel({ slides, intervalMs = 3000 }) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);
  const navigate = useNavigate();
  const N = slides.length;

  const goTo = useCallback((idx) => {
    setActive(((idx % N) + N) % N);
  }, [N]);

  const next = useCallback(() => goTo(active + 1), [active, goTo]);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);

  // Auto-advance
  useEffect(() => {
    if (paused || N < 2) return;
    const id = setInterval(() => setActive((i) => (i + 1) % N), intervalMs);
    return () => clearInterval(id);
  }, [paused, N, intervalMs]);

  // Touch handlers
  const onTouchStart = (e) => {
    setPaused(true);
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };
  const onTouchEnd = () => {
    setPaused(false);
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
  };

  return (
    <section
      // iter66 — TRULY responsive hero.
      //
      // Root cause of the S24 Ultra side-gap bug: the previous layout
      // pinned the section height with `vh` units and rendered the mobile
      // image with `object-contain`.  Because `vh` evaluates differently
      // across browsers/phones (Chrome vs Samsung Internet, notch vs no
      // notch), the container's aspect ratio never exactly matches the
      // uploaded image's aspect ratio — and `object-contain` reveals that
      // mismatch as empty left/right bars.
      //
      // New strategy — width-first, aspect-ratio driven:
      //   • Mobile: fix the section's aspect to `12/17` (the ratio our
      //     hero images are actually shot in) and cap height at
      //     `100svh` for landscape.  Because width is always `100vw`,
      //     height is derived — never dictated by `vh`.  Every phone,
      //     regardless of DPI or chrome height, gets an edge-to-edge
      //     image with the exact same composition.
      //   • Tablet+ (>=sm): fall through to `16/9` which matches the
      //     `img_desktop` variant admins upload.
      //   • Image: `object-cover` on every breakpoint so any residual
      //     width/height rounding mismatch is absorbed by a hairline
      //     cover-crop of the (safe) outer background — never a bar.
      //   • `objectPosition: center` keeps the products / heading /
      //     CTA — which live in the middle of the composition — in view
      //     across every device from 320px to 1920px.
      //
      // No JS, no ResizeObserver, no per-device forks; just CSS.
      className="relative w-full aspect-[12/17] max-h-[100svh] sm:aspect-[16/9] sm:max-h-none sm:h-auto sm:min-h-[560px] lg:min-h-[680px] overflow-hidden bg-brand-dark"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
      data-testid="hero-carousel"
    >
      {/* SLIDES TRACK */}
      <div
        ref={trackRef}
        className="absolute inset-0 flex will-change-transform"
        style={{
          transform: `translate3d(-${active * 100}%, 0, 0)`,
          transitionProperty: "transform",
          transitionDuration: "900ms",
          transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {slides.map((s, i) => (
          <Slide
            key={s.id}
            slide={s}
            index={i}
            active={active === i}
            navigate={navigate}
          />
        ))}
      </div>

      {/* DOTS */}
      <div
        className="absolute bottom-6 sm:bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5"
        data-testid="hero-dots"
      >
        {slides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => goTo(i)}
            className={`h-2 rounded-full transition-all duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-turmeric/60 ${
              active === i
                ? "w-8 bg-brand-turmeric"
                : "w-2 bg-brand-cream/40 active:bg-brand-cream/70 [@media(hover:hover)]:hover:bg-brand-cream/70"
            }`}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            data-active={active === i ? "true" : "false"}
          />
        ))}
      </div>
    </section>
  );
}

function Slide({ slide, index, active, navigate }) {
  const isFirst = index === 0;

  const CTA = slide.ctaLabel ? (
    <div
      className={`transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      style={{ transitionDelay: active ? "450ms" : "0ms" }}
    >
      <button
        type="button"
        onClick={() => navigate(slide.ctaHref || "/shop")}
        className="btn-primary !px-6 sm:!px-9 !py-3 sm:!py-4 !text-sm sm:!text-base"
        data-testid={`hero-cta-${index}`}
      >
        {slide.ctaLabel} <ArrowRight className="ml-2 w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  ) : null;

  return (
    <div
      className="relative shrink-0 w-full h-full"
      role="group"
      aria-roledescription="slide"
      data-testid={`hero-slide-${index}`}
    >
      {/* iter41 → iter66: responsive hero image.  `<picture>` still swaps
          the desktop 16:9 source at >=768px so the browser only downloads
          one image.  `object-cover` on EVERY breakpoint guarantees the
          image fills width edge-to-edge with no side gaps — the container
          aspect ratio (12/17 mobile, 16/9 desktop) is chosen to match
          the shot composition so the absorbed cover-crop is minuscule. */}
      <picture data-testid={`hero-picture-${index}`}>
        {slide.img_desktop && (
          <source
            media="(min-width: 768px)"
            srcSet={imageUrl(slide.img_desktop, { width: 1600 })}
          />
        )}
        <img
          src={imageUrl(slide.img, { width: 800 })}
          alt={slide.alt || slide.title}
          loading={index === 0 ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={index === 0 ? "high" : "auto"}
          draggable={false}
          className="absolute inset-0 w-full h-full object-cover select-none bg-brand-dark"
          style={{ objectPosition: slide.objectPosition || "center" }}
          data-testid={`hero-img-${index}`}
        />
      </picture>

      {/* SLIDE 1 — keep existing layout (text bottom-aligned mobile, centered desktop). */}
      {isFirst && (
        <>
          {/* iter101 — the "NELLORE, ANDHRA • SINCE 2002" top-left tagline
              was removed at customer request.  Nothing replaces it — the
              slide's main headline + CTA remain unchanged. */}

          <div className="relative h-full container-x flex items-end sm:items-center pb-12 sm:pb-0">
            <div className="max-w-2xl text-white">
              <h1
                className={`font-serif text-3xl sm:text-5xl lg:text-7xl leading-[1.05] sm:leading-[0.95] mb-3 sm:mb-6 font-semibold transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                style={{
                  transitionDelay: active ? "250ms" : "0ms",
                  textShadow: "0 4px 24px rgba(0,0,0,0.7), 0 2px 6px rgba(0,0,0,0.55)",
                }}
              >
                {slide.titleNode || slide.title}
              </h1>
              {slide.subtitle && (
                <p
                  className={`hidden sm:block text-base sm:text-lg text-brand-cream/95 max-w-lg leading-relaxed mb-10 transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                  style={{
                    transitionDelay: active ? "350ms" : "0ms",
                    textShadow: "0 2px 12px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.5)",
                  }}
                >
                  {slide.subtitle}
                </p>
              )}
              {CTA}
            </div>
          </div>
        </>
      )}

      {/* SLIDE 2+ — text upper-left, CTA bottom-right. */}
      {!isFirst && (
        <div className="relative h-full container-x">
          <div className="absolute top-14 sm:top-20 lg:top-24 left-4 sm:left-8 lg:left-12 right-4 sm:right-auto max-w-xl text-white">
            {slide.overline && (
              <span
                className={`overline !text-brand-turmeric mb-2 sm:mb-5 block text-xs sm:text-sm lg:text-base transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
                style={{
                  transitionDelay: active ? "150ms" : "0ms",
                  textShadow: "0 2px 12px rgba(0,0,0,0.65), 0 1px 2px rgba(0,0,0,0.55)",
                }}
              >
                {slide.overline}
              </span>
            )}
            <h1
              className={`font-serif text-3xl sm:text-5xl lg:text-7xl leading-[1.05] sm:leading-[0.95] mb-3 sm:mb-5 font-semibold transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              style={{
                transitionDelay: active ? "250ms" : "0ms",
                textShadow: "0 4px 24px rgba(0,0,0,0.7), 0 2px 6px rgba(0,0,0,0.55)",
              }}
            >
              {slide.titleNode || slide.title}
            </h1>
            {slide.subtitle && (
              <p
                className={`hidden sm:block text-base sm:text-lg text-brand-cream/95 max-w-lg leading-relaxed transition-all duration-700 ${active ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                style={{
                  transitionDelay: active ? "350ms" : "0ms",
                  textShadow: "0 2px 12px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.5)",
                }}
              >
                {slide.subtitle}
              </p>
            )}
          </div>
          {CTA && (
            <div className="absolute bottom-16 sm:bottom-20 lg:bottom-24 right-4 sm:right-8 lg:right-12">
              {CTA}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
